import {
  type Category,
  getFirstAttributeValue,
  slugify,
  type Product,
  type ProductSuggestion,
  type BrandOrCategorySuggestion,
} from '@scayle/storefront-nuxt'
import useRouteHelpers from '#layers/base/composables/useRouteHelpers'
import type {
  LocationQuery,
  RouteLocation,
  RouteLocationRaw,
} from '#vue-router'

const getCategoryPath = (category: Category) => {
  if (!category) {
    return
  }
  return `${category.path}`
}

export const getPlpPath = (slug: string | string[]) => {
  if (Array.isArray(slug)) {
    return `/${slug.join('/')}`
  }
  return `/${slug}`
}

export const normalizePathRoute = (path: string) => {
  return path.startsWith('/') ? path : `/${path}`
}

export const hasLocalePrefix = (path: string, prefix?: string) => {
  const components = path.split('/')
  return components[1] && components[1] === prefix
}

export const getProductDetailRoute = (
  product: Product | OrderProduct,
  id?: number,
  queryParams?: LocationQuery,
): RouteLocationRaw => {
  const name = getFirstAttributeValue(product.attributes, 'name')?.label ?? ''

  return {
    name: 'p-slug-id',
    params: {
      slug: slugify(name),
      id: String(id || product.id),
    },
    query: queryParams,
  }
}

export const getProductDetailRouteLocalized = (
  locale: string,
  product: Product,
  id?: number,
) => {
  const name = getFirstAttributeValue(product?.attributes, 'name')?.label ?? ''

  return {
    name: 'p-slug-id',
    params: {
      slug: slugify(name),
      id: id || product?.id,
    },
  }
}

export const getOrderProductDetailRoute = (
  product: OrderProduct,
  id?: number,
): RouteLocationRaw => {
  const name = product.attributes.name.label
  const { toLocalePath } = useRouteHelpers()

  return toLocalePath({
    name: 'p-slug-id',
    params: {
      slug: `${slugify(name)}-${id || product.id}`,
    },
  })
}

export const getProductDetailPath = (product: Product, id?: number) => {
  const { toLocalePath } = useRouteHelpers()

  const name = getFirstAttributeValue(product.attributes, 'name')?.label ?? ''
  return toLocalePath(`/p/${slugify(name)}-${id || product.id}`)
}

export const getSearchRoute = (
  term: string,
  forceNoProducts: boolean,
): RouteLocationRaw => {
  const { toLocalePath } = useRouteHelpers()

  return toLocalePath({
    name: 'search',
    query: { term, ...(forceNoProducts ? { nop: '1' } : {}) },
  })
}

export const getSearchSuggestionPath = (
  suggestion: ProductSuggestion | BrandOrCategorySuggestion,
) => {
  if (!suggestion) {
    return
  }

  if ('product' in suggestion) {
    return getProductDetailPath(suggestion.product)
  }

  const { toLocalePath } = useRouteHelpers()

  const { category, brand } = suggestion
  const route =
    category && brand
      ? `${getCategoryPath(category)}?brand=${brand?.id}`
      : getCategoryPath(category)

  return route && toLocalePath(route)
}

export const getOrderDetailsRoute = (id: number): RouteLocationRaw => {
  const { toLocalePath } = useRouteHelpers()

  return toLocalePath({
    name: routeList.orderDetail.name,
    params: { id },
  })
}

export const isExactActiveRoute = (
  route: RouteLocation,
  toCompare: string,
  options = { ignoreQuery: false },
) => {
  const compareUrl =
    options.ignoreQuery && toCompare.includes('?')
      ? toCompare.slice(0, toCompare.indexOf('?') + 1)
      : toCompare
  return route.path === compareUrl
}

type Link =
  | 'search'
  | 'wishlist'
  | 'basket'
  | 'product'
  | 'stores'
  | 'lookbooks'
  | 'trends'
  | 'home'
  | 'checkout'
  | 'signin'
  | 'account'
  | 'orders'
  | 'orderDetail'
  | 'user'
  | 'signout'
  | 'newsletterSuccess'
  | 'releasetestpage'

export type LinkList = Record<
  Link,
  { name: string; path: string; parameter?: string }
>

export const routeList: LinkList = {
  home: { name: 'index', path: '/' },
  releasetestpage: { name: 'index', path: '/releasetestpage' },
  search: { name: 'search', path: '/search' },
  wishlist: { name: 'wishlist', path: '/wishlist' },
  basket: { name: 'basket', path: '/basket' },
  product: { name: 'product-slug', path: '/product' },
  stores: { name: 'stores-slug', path: '/stores' },
  trends: { name: 'women-trends-slug', path: '/women/trends' },
  lookbooks: {
    name: 'women-lookbooks-slug',
    path: '/women/lookbooks',
    parameter: 'slug',
  },
  signin: { name: 'signin', path: '/signin' },
  signout: { name: 'signout', path: '/signout' },
  account: { name: 'account', path: '/account' },
  checkout: { name: 'checkout', path: '/checkout' },
  orders: { name: 'account-orders', path: '/account/orders' },
  user: { name: 'account-user', path: '/account/user' },
  orderDetail: {
    name: 'account-orders-id',
    path: '/account/orders',
    parameter: 'id',
  },
  newsletterSuccess: {
    name: 'newsletter-success',
    path: '/c/service/newsletter/thank-you',
  },
} as const

export const getAdvantageProductDetailPath = (
  product: Product,
  orderNumber: string,
) => {
  const { toLocalePath } = useRouteHelpers()
  const name = getFirstAttributeValue(product.attributes, 'name')?.label ?? ''

  return toLocalePath(
    `/p/${slugify(name)}/${product.id}?ordernumber=${orderNumber}`,
  )
}

export { getCategoryPath }
